import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Check } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Step1CertificateDetails from "@/components/Step1CertificateDetails";
import Step2AddressCustomer from "@/components/Step2AddressCustomer";
import Step3WorkItems from "@/components/Step3WorkItems";
import Step4Summary from "@/components/Step4Summary";

const STEPS = [
  { num: 1, label: "Certificate Details" },
  { num: 2, label: "Address & Customer" },
  { num: 3, label: "Add Work" },
  { num: 4, label: "Summary & Payment" },
];

const INITIAL_FORM = {
  certificate_ref: "",
  installation_address: "",
  description: "",
  installation_date: null,
  country: "United Kingdom",
  region: "England and Wales",
  is_new_build: false,
  premise: "",
  street: "",
  locality: "",
  town: "",
  county: "",
  postcode: "",
  local_authority: "",
  local_authority_email: "",
  homeowner_email: "",
  homeowner_name: "",
  homeowner_phone: "",
  authority_confirmed: false,
  works: [
    { name: "Circuit alteration or addition in a kitchen/special location", quantity: 0 },
    { name: "Install one or more new circuits", quantity: 0 },
    { name: "New full electrical installation (new build)", quantity: 0 },
    { name: "Partial rewire", quantity: 0 },
    { name: "Replacement consumer unit", quantity: 0 },
    { name: "Rewire of all circuits", quantity: 0 },
  ],
};

export default function NotificationWizard() {
  const { user, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(() => {
    const repeatData = sessionStorage.getItem("repeat_notification_data");
    if (repeatData) {
      sessionStorage.removeItem("repeat_notification_data");
      try {
        const prefill = JSON.parse(repeatData);
        return { ...INITIAL_FORM, ...prefill };
      } catch { /* ignore */ }
    }
    return INITIAL_FORM;
  });

  const updateForm = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const nextStep = () => setStep((s) => Math.min(s + 1, 4));
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="notification-wizard">
      <AppHeader />

      {/* Step Indicator */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-8" data-testid="step-indicator">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div
                  data-testid={`step-${s.num}-indicator`}
                  className={`w-10 h-10 flex items-center justify-center border-2 text-sm font-bold transition-all ${
                    step > s.num
                      ? "step-completed"
                      : step === s.num
                      ? "step-active"
                      : "step-inactive"
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span
                  className={`text-xs mt-2 font-medium text-center hidden sm:block ${
                    step >= s.num ? "text-[#0A0A0A]" : "text-[#B8B9BD]"
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 sm:mx-4 ${step > s.num ? "bg-[#D4A537]" : "bg-[#E4E4E7]"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="animate-fade-in-up" key={step}>
          {step === 1 && <Step1CertificateDetails formData={formData} updateForm={updateForm} onNext={nextStep} />}
          {step === 2 && <Step2AddressCustomer formData={formData} updateForm={updateForm} onNext={nextStep} onPrev={prevStep} />}
          {step === 3 && <Step3WorkItems formData={formData} updateForm={updateForm} onNext={nextStep} onPrev={prevStep} user={user} />}
          {step === 4 && <Step4Summary formData={formData} onPrev={prevStep} user={user} />}
        </div>
      </div>
    </div>
  );
}
